import { useState, useEffect } from 'react';
import { Download, ZoomIn, ZoomOut, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api, type CandidateFile } from '@/lib/api';
import { notifyApiError, getFriendlyErrorMessage } from '@/lib/api-error';

interface DocumentViewerProps {
  file: CandidateFile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Fetch a file with auth headers and return a blob URL for preview.
 */
async function fetchAuthenticatedBlobUrl(fileId: string): Promise<string> {
  const { blob } = await api.requestBlob(`/files/${fileId}`);
  return URL.createObjectURL(blob);
}

export function DocumentViewer({ file, open, onOpenChange }: DocumentViewerProps) {
  const [zoom, setZoom] = useState(1);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch authenticated blob URL when file changes
  useEffect(() => {
    if (!file || !open) {
      // Clean up previous blob URL
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
      setError(null);
      setZoom(1);
      return;
    }

    const isPreviewable =
      file.mimeType.startsWith('image/') || file.mimeType === 'application/pdf';

    if (!isPreviewable) {
      setBlobUrl(null);
      return;
    }

    setLoading(true);
    setError(null);

    fetchAuthenticatedBlobUrl(file.id)
      .then((url) => {
        setBlobUrl(url);
        setLoading(false);
      })
      .catch((err) => {
        setError(getFriendlyErrorMessage(err));
        setLoading(false);
      });

    // Cleanup on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.id, open]);

  if (!file) return null;

  const isImage = file.mimeType.startsWith('image/');
  const isPdf = file.mimeType === 'application/pdf';

  const handleDownload = () => {
    // Download also needs auth, so fetch as blob
    fetchAuthenticatedBlobUrl(file.id)
      .then((url) => {
        const a = document.createElement('a');
        a.href = url;
        a.download = file.originalName || file.fileName || 'download';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(notifyApiError);
  };

  const zoomIn = () => setZoom((z) => Math.min(z + 0.25, 3));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.25, 0.5));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="truncate pr-4">{file.originalName}</DialogTitle>
            <div className="flex items-center gap-2">
              {(isImage || isPdf) && (
                <>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={zoomOut} title="Zoom out">
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-text-tertiary min-w-[3rem] text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={zoomIn} title="Zoom in">
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto min-h-0">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 text-text-tertiary animate-spin" />
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-64 text-text-tertiary">
              <p className="text-lg mb-2">Failed to load preview</p>
              <p className="text-sm mb-4">{error}</p>
              <Button onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download Instead
              </Button>
            </div>
          )}

          {!loading && !error && isPdf && blobUrl && (
            <div className="w-full h-full flex items-center justify-center" style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}>
              <iframe
                src={`${blobUrl}#toolbar=1`}
                className="w-full border-0"
                style={{ height: '70vh' }}
                title={file.originalName}
              />
            </div>
          )}

          {!loading && !error && isImage && blobUrl && (
            <div className="flex items-center justify-center p-4 overflow-auto">
              <img
                src={blobUrl}
                alt={file.originalName}
                className="max-w-full transition-transform"
                style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
              />
            </div>
          )}

          {!isPdf && !isImage && (
            <div className="flex flex-col items-center justify-center h-64 text-text-tertiary">
              <p className="text-lg mb-2">Preview not available</p>
              <p className="text-sm mb-4">This file type cannot be previewed in the browser.</p>
              <Button onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download to View
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
